import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'
import type { EditorSyncState } from './types'
import {
  editorInitialized,
  editorMerged,
  editorCleared,
  slideSyncPending,
  slideSyncFulfilled,
  slideSyncRejected,
} from './actions'

const initialState: EditorSyncState = {
  storyId: null,
  storyTitle: null,
  nextTempId: -1,
  isSyncing: false,
  syncError: null,
  lastSyncedAt: null,
}

export const editorSyncSlice = createSlice({
  name: 'editorSync',
  initialState,
  reducers: {
    decrementTempId(state) {
      state.nextTempId -= 1
    },
    setStoryTitle(state, action: PayloadAction<string>) {
      state.storyTitle = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(editorInitialized, (state, action) => {
        state.storyId = action.payload.storyId
        state.storyTitle = action.payload.storyTitle ?? null
        state.nextTempId = -1
        state.isSyncing = false
        state.syncError = null
      })
      .addCase(editorMerged, (state) => {
        // Clear stale isSyncing rehydrated from a sync that never resolved
        // (e.g. tab closed mid-request). Without this, the editor is stuck
        // showing "Saving..." with the Save button disabled forever.
        state.isSyncing = false
        state.syncError = null
      })
      .addCase(editorCleared, () => initialState)
      .addCase(slideSyncPending, (state) => {
        state.isSyncing = true
        state.syncError = null
      })
      .addCase(slideSyncFulfilled, (state) => {
        state.isSyncing = false
        state.lastSyncedAt = new Date().toISOString()
      })
      .addCase(slideSyncRejected, (state, action) => {
        state.isSyncing = false
        state.syncError = action.payload.error
      })
  },
})

export const { decrementTempId, setStoryTitle } = editorSyncSlice.actions
